import { apiDelete, apiGet, apiPost } from './api'

const SW_PATH = '/sw.js'

let serviceWorkerRegistrationPromise = null

function isLocalhost() {
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
}

export function isStandalonePwa() {
  return window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
}

export function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || '') ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
}

export function isAndroid() {
  return /android/i.test(window.navigator.userAgent || '')
}

export function canUsePushApis() {
  return Boolean(
    typeof window !== 'undefined' &&
    (window.isSecureContext || isLocalhost()) &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function pushPermission() {
  if (!('Notification' in window)) return 'unsupported'
  return Notification.permission
}

export async function registerPortalServiceWorker({ allowDev = false } = {}) {
  if (!('serviceWorker' in navigator)) return null
  if (!allowDev && !import.meta.env.PROD && import.meta.env.VITE_ENABLE_PWA_DEV !== 'true') return null
  if (!serviceWorkerRegistrationPromise) {
    serviceWorkerRegistrationPromise = navigator.serviceWorker.register(SW_PATH, { scope: '/' }).then(registration => {
      registration.update?.().catch(() => {})
      return registration
    })
  }
  return serviceWorkerRegistrationPromise
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)))
}

export async function getPortalPushConfig() {
  return apiGet('/api/mso/push/public-key')
}

export async function getExistingPushSubscription({ allowDev = false } = {}) {
  const registration = await registerPortalServiceWorker({ allowDev })
  if (!registration) return null
  return registration.pushManager.getSubscription()
}

export async function subscribeToPortalPush({ publicKey, allowDev = false } = {}) {
  if (!canUsePushApis()) return { ok: false, reason: 'unsupported' }
  if (!publicKey) return { ok: false, reason: 'missing_public_key' }

  const registration = await registerPortalServiceWorker({ allowDev })
  if (!registration) return { ok: false, reason: 'service_worker_unavailable' }

  let permission = Notification.permission
  if (permission === 'default') {
    permission = await Notification.requestPermission()
  }
  if (permission !== 'granted') return { ok: false, permission, reason: 'permission_not_granted' }

  const existing = await registration.pushManager.getSubscription()
  const subscription = existing || await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  })

  await apiPost('/api/mso/push/subscribe', {
    subscription: subscription.toJSON(),
    context: {
      standalone: isStandalonePwa(),
      platform: isIos() ? 'ios' : isAndroid() ? 'android' : 'web',
      userAgent: window.navigator.userAgent,
    },
  })

  return { ok: true, permission: 'granted', subscription }
}

export async function unsubscribeFromPortalPush({ allowDev = false } = {}) {
  const subscription = await getExistingPushSubscription({ allowDev })
  if (!subscription) return { ok: true, unsubscribed: false }
  await apiDelete('/api/mso/push/subscribe', { endpoint: subscription.endpoint }).catch(() => {})
  await subscription.unsubscribe()
  return { ok: true, unsubscribed: true }
}
